import { EditIcon } from '@diligentcorp/atlas-react-icons';
import { UserDto } from '@issuemanager/api-client';
import {
  DataGridDateFilter,
  DataGridIssueSubtypeByTypeManagerFilter,
  DataGridIssueTypeByTypeManagerFilter,
  DataGridUserFilter,
  DateWithTime,
  type ExtendedGridColDef,
  GridHeader,
} from '@issuemanager/apps/client/components';
import { paths } from '@issuemanager/apps/client/paths';
import { useLocalizedIssueTypes } from '@issuemanager/client/features/issues';
import {
  ISSUE_TYPES_FIELD_CREATED,
  ISSUE_TYPES_FIELD_CREATED_BY,
  ISSUE_TYPES_FIELD_ISSUE_SUBTYPE,
  ISSUE_TYPES_FIELD_ISSUE_TYPE,
  ISSUE_TYPES_FIELD_ISSUE_TYPE_MANAGER,
  IssueType,
} from '@issuemanager/constants';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const commonIssueTypesGridColumnsConfig = {
  minWidth: 150,
};

export const useIssueTypesGridColumns = () => {
  const { t } = useTranslation(['settings']);
  const navigate = useNavigate();
  const localizedIssueTypes = useLocalizedIssueTypes();

  const column = useCallback(
    (props: ExtendedGridColDef) => ({
      renderHeader: GridHeader,
      ...commonIssueTypesGridColumnsConfig,
      ...props,
    }),
    [],
  );

  const columns = useMemo(
    () => [
      column({
        field: ISSUE_TYPES_FIELD_ISSUE_TYPE,
        headerName: t('settings:issueTypes.table.header.issueType'),
        valueGetter: (params) =>
          localizedIssueTypes[params.row.issueTypeName as IssueType] ??
          params.row.issueTypeName,
        renderHeaderFilter: DataGridIssueTypeByTypeManagerFilter,
        hideable: false,
      }),
      column({
        field: ISSUE_TYPES_FIELD_ISSUE_SUBTYPE,
        headerName: t('settings:issueTypes.table.header.issueSubtype'),
        valueGetter: (params) => params.row.issueSubtypeName,
        renderHeaderFilter: DataGridIssueSubtypeByTypeManagerFilter,
      }),
      column({
        field: ISSUE_TYPES_FIELD_ISSUE_TYPE_MANAGER,
        headerName: t('settings:issueTypes.table.header.issueTypeManagers'),
        valueGetter: (params) =>
          params.value?.map((manager: UserDto) => manager.name).join(', '),
        renderHeaderFilter: DataGridUserFilter,
        sortable: false,
      }),
      column({
        field: ISSUE_TYPES_FIELD_CREATED_BY,
        headerName: t('settings:issueTypes.table.header.createdBy'),
        valueGetter: (params) => params.value,
        renderHeaderFilter: DataGridUserFilter,
      }),
      column({
        field: ISSUE_TYPES_FIELD_CREATED,
        headerName: t('settings:issueTypes.table.header.createdAt'),
        renderCell: (params) => <DateWithTime date={params.value} />,
        renderHeaderFilter: DataGridDateFilter,
      }),
      column({
        field: 'Edit',
        headerName: '',
        renderCell: (params) => {
          const { issueTypeId } = params.row;
          const { issueSubtypeId } = params.row;
          const searchParams = new URLSearchParams();
          if (issueTypeId) searchParams.append('issueTypeId', issueTypeId);
          if (issueSubtypeId) {
            searchParams.append('issueSubtypeId', issueSubtypeId);
          }
          return (
            <EditIcon
              onClick={() => {
                navigate(
                  `/${paths.settings.index}/${paths.issueTypes.index}/${
                    paths.issueTypes.edit
                  }?${searchParams.toString()}`,
                );
              }}
            />
          );
        },
        sortable: false,
        maxWidth: 51,
        align: 'center',
        type: 'checkboxSelection',
        hideable: false,
      }),
    ],
    [t],
  );

  return { columns };
};
